import { useState, useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import type { EventPanelMetadata } from '@mark43/cad-api';
import { useEnabledFeatures } from '../../common/hooks/enabled-features/use-enabled-features';
import { PriorityLevel } from './types';
import { FrEventFormType } from '../../fr-entity-panels/types';
import { globalAttributes } from '../../common/global-attributes';
import { useEventMetadataWithTrackedKeys } from '../../event-panel/event-panel-form/event-metadata';

const PRIORITY_METADATA_KEYS: readonly (keyof EventPanelMetadata)[] = ['agencyTypeGlobalAttrId'];

export function useEventPriority() {
    const [level, setLevel] = useState<PriorityLevel>(4);
    const { control } = useFormContext<FrEventFormType>();
    const eventPriority = useWatch({ control, name: 'priority' });
    const agencyTypeGlobalAttrId =
        useEventMetadataWithTrackedKeys(PRIORITY_METADATA_KEYS)?.agencyTypeGlobalAttrId;

    useEffect(() => {
        if (!eventPriority?.parentAttributeId) {
            return;
        }

        const level = eventPriority.parentAttributeId % 100;

        if (level && level > 0) {
            setLevel(level > 4 ? 4 : level);
        }
    }, [eventPriority]);

    const label = eventPriority?.label;
    const priorityId = eventPriority?.value;
    return useMemo(
        function buildEventPriority() {
            return { label, level, priorityId, agencyTypeGlobalAttrId };
        },
        [label, level, priorityId, agencyTypeGlobalAttrId]
    );
}

export function useRiskLevel() {
    const [level, setLevel] = useState<PriorityLevel>(4);
    const { control } = useFormContext<FrEventFormType>();
    const riskLevel = useWatch({ control, name: 'riskLevel' });
    const { eventsHaveRiskLevel } = useEnabledFeatures();

    useEffect(() => {
        if (!eventsHaveRiskLevel || !riskLevel?.parentAttributeId) {
            return;
        }

        const level =
            Object.values(globalAttributes.riskLevelGlobal)
                .reverse()
                .findIndex(
                    (parentAttributeId) => parentAttributeId === riskLevel.parentAttributeId
                ) + 1;

        if (level && level > 0) {
            setLevel(level > 4 ? 4 : level);
        }
    }, [riskLevel, eventsHaveRiskLevel]);

    if (!eventsHaveRiskLevel) {
        return null;
    }

    return { label: riskLevel?.label, level };
}
